import * as MdIcons from "react-icons/md"
import { MdDelete } from "react-icons/md"
import styled from 'styled-components'
import { useState, useEffect } from "react";
import { ControlledTabs } from "../../../../Components/Tabs/Tabs";
import { Column } from "../../../../Components/Layout/Column";
import { TabSelector } from "../../../../Components/Tabs/TabSelector";
import { Annotation } from "../../Variables/Annotations";
import { DEFAULT_ANNOTATION_PROPERTIES } from "../../Variables/Annotations";
import { MobergCheckbox } from "../../../../Components/MobergButton/MobergCheckbox";
import { useModalProvider } from "../../../../Providers/ModalProvider";
import ResetToDefaultModal from "./ConfirmationModals/ResetToDefaultModal";
import DeleteKeyboardShortcutModal from "./ConfirmationModals/DeleteKeyboardShortcutModal";
import ModalHeader from "../../../../Components/ModalHeader/ModalHeader";
import { useSetRecoilState, useRecoilValue } from "recoil"
import { hotkeyTabs } from "../../../../Pages/Data/Visualize/DataReview/Types/KeyboardShortcut";
import { 
	generalShortcutsAtom, 
	annotationShortcutsAtom,
	hotkeysEnabledAtom,
} from "../../../../Pages/Data/Visualize/DataReview/Atoms/Hotkeys";
import { selectedAnnotationAtom } from "../../../../Pages/Data/Visualize/DataReview/Atoms/Annotations";
import { EditAnnotation } from "../Components/EditAnnotation";
import { useDataReviewPermissions } from "../../../../Hooks/useDataReviewPermissions";
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton";
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors";
import { MobergFontSize } from "../../../../Components/MobergFont/MobergFont";
import { MobergIconSize } from "../../../../Components/MobergIcon/MobergIcon";
import { useAnnotationShortcutService } from "../../../../Hooks/useAnnotationShortcutService";

export function HotkeysModal() {
    // Use service for CRUD operations
    const { createAnnotationShortcut, deleteAnnotationShortcuts, updateAnnotationShortcut, resetAnnotationShortcuts } = useAnnotationShortcutService();
    const { createModal, close } = useModalProvider()
const { canManageDataReview } = useDataReviewPermissions()

const [selectedTab, setSelectedTab] = useState(hotkeyTabs.GENERAL)
    const [selectedShortcutIds, setSelectedShortcutIds] = useState([])
    const generalShortcuts = useRecoilValue(generalShortcutsAtom)
    const annotationShortcuts = useRecoilValue(annotationShortcutsAtom)
    const setHotkeysEnabled = useSetRecoilState(hotkeysEnabledAtom)
    const setSelectedAnnotation = useSetRecoilState(selectedAnnotationAtom)


    useEffect(() => {
        setSelectedShortcutIds(prevState => prevState.filter(selectedAnnotation => annotationShortcuts.includes(selectedAnnotation)))
    }, [annotationShortcuts])

    function renderResetToDefaultModal() {
        createModal(
            <ResetToDefaultModal
                escClose={false}
                clickOutClose={false}
                onConfirm={() => {
                    resetAnnotationShortcuts();
                }}
            />
        )
    }

    function renderDeleteAnnotationModal() {
        createModal(
            <DeleteKeyboardShortcutModal
                escClose={false}
                clickOutClose={false}
                onConfirm={() => {
                    if (selectedShortcutIds.length > 0) {
                        deleteAnnotationShortcuts(selectedShortcutIds);
                        setSelectedShortcutIds([]);
                    }
                }}
            />
        )
    }

    function exitShortcutsModalNoChanges() {
        setHotkeysEnabled(true)
        close()
    }

    function getPrettyHotkeyTrigger(config) {
        if (config.triggers[0] === null) {
            return null
        }

        const trigger = config.triggers[0].toLowerCase()

        switch (trigger) {
            case "arrowright": return <MdIcons.MdArrowRightAlt />
            case "arrowleft": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(180deg)" }} />
            case "arrowdown": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(90deg)" }} />
            case "arrowup": return <MdIcons.MdArrowRightAlt style={{ transform: "rotate(270deg)" }} />
            case "escape": return "Esc"
            case " ": return "spacebar"
            default: return trigger
        }
    }

    function renderEditAnnotationModal(config, mode) {
        if (mode === 'CREATE') {
            config.isInEditMode = true
            config.triggers = [null, null]
        }

        createModal(
            <EditAnnotation
                escClose={false}
                clickOutClose={false}
                config={config}
                mode={mode}
                isHotkeysModal={true}
                onSave={(data) => (mode === 'CREATE') ? createAnnotationShortcut(data) : updateAnnotationShortcut(data.id, data)}
                hotkeyConfigs={annotationShortcuts}
            />
        )
    }

    function openCustomAnnotationModal(config, mode) {
        setSelectedAnnotation(Annotation.fromConfig(config))
        renderEditAnnotationModal(config, mode)
    }

    function onClickShortcutCheckbox(nextState, shortcutId) {
        nextState 
            ? setSelectedShortcutIds(prevState => [...prevState, shortcutId]) 
            : setSelectedShortcutIds(prevState => prevState.filter(selectedAnnotation => selectedAnnotation !== shortcutId))
    }

    return (
        <div style={{ overflow: "visible" }}>
            <div style={{ width: "600px", maxHeight: "650px", background: "var(--color-surface-panel)", borderRadius: "6px", border: "none", zIndex: "950px", display: 'flex', flexDirection: 'column', minHeight: '500px', color: "var(--color-text-primary)" }}>
                <ModalHeader headerText="Keyboard Shortcuts" hrStyle={{ display: "none" }}
                    closeFxn={exitShortcutsModalNoChanges}
                />

                <div style={{ paddingLeft: "15px" }}>
                    <ControlledTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab}>
                        <Column style={{ paddingLeft: "15px" }}>
                            <TabSelector tabKeys={Object.values(hotkeyTabs)} style={{ color: "var(--text-secondary-color)" }} permission={canManageDataReview} />
                        </Column>
                    </ControlledTabs>
                </div>

                <hr style={{ border: "1px solid var(--color-border-default)", margin: 0 }} />

                <ModalContentVertical>
                    <div style={{ height: "40px", marginBottom: "16px", display: selectedTab === 'General' ? "none" : "flex" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", flex: 1 }}>
                            <MobergButton theme={MobergTheme.BLUE} variant={MobergButtonVariant.FILLED} fontSize={MobergFontSize.LARGE}
                                onClick={() => { openCustomAnnotationModal(DEFAULT_ANNOTATION_PROPERTIES, "CREATE") }}>
                                Add new shortcut
                            </MobergButton>

                            <div style={{ display: "flex", gap: "8px" }}>
                                <MobergButton
                                    theme={MobergTheme.RED}
                                    variant={MobergButtonVariant.FILLED}
                                    shape={MobergButtonShape.SQUARE}
                                    disabled={selectedShortcutIds.length === 0}
                                    onClick={renderDeleteAnnotationModal}>
                                    <MdDelete size={MobergIconSize.REGULAR} />
                                </MobergButton>

                                <MobergButton
                                    theme={MobergTheme.BLUE}
                                    variant={MobergButtonVariant.OUTLINE}
                                    shape={MobergButtonShape.SQUARE}
                                    fontSize={MobergFontSize.LARGE}
                                    onClick={renderResetToDefaultModal}>
                                    Reset to default
                                </MobergButton>
                            </div>
                        </div>
                    </div>
                </ModalContentVertical>

                {selectedTab === 'General' && (
                    generalShortcuts.length > 0 ? (
                        <ShortcutsContainer style={{ display: "flex", flexDirection: 'column', alignItems: 'center' }}>
                            {generalShortcuts.map(shortcut => (
                                <AnnotationDiv 
                                    key={shortcut.id}
                                    title={!canManageDataReview && shortcut.title.toLowerCase().includes('annotation') ? 'You do not have permission for this action' : ''} 
                                    style={{ opacity: !canManageDataReview && shortcut.title.toLowerCase().includes('annotation') ? 0.5 : 1 }}
                                >
                                    <p style={{ margin: 0 }}>{shortcut.title}</p>
                                    <KeyboardButton>
                                        {getPrettyHotkeyTrigger(shortcut)}
                                    </KeyboardButton>
                                </AnnotationDiv>
                            ))}
                        </ShortcutsContainer>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                            <h1>No Shortcuts</h1>
                        </div>
                    )
                )}

                {selectedTab === 'Annotations' && (
                    annotationShortcuts.length > 0 ? (
                        <ShortcutsContainer style={{ display: "flex", flexDirection: 'column', alignItems: 'center' }}>
                            {annotationShortcuts.map(config => (
                                <AnnotationDiv key={config.id} backgroundColor={selectedShortcutIds.includes(config.id) ? "var(--color-hover-neutral)" : "none"}>
                                    <div style={{ display: 'flex', alignItems: 'center', width: "100%" }}>
                                        <MobergCheckbox
                                            isChecked={selectedShortcutIds.includes(config.id)}
                                            onChange={(nextState) => onClickShortcutCheckbox(nextState, config.id)}
                                        />

                                        <p title={config.data?.text || "Custom Annotation"} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>
                                            {config.data?.text || "Custom Annotation"}
                                        </p>

                                        <ColorCircle style={{ marginLeft: '5px' }} color={config.data?.color} opacity={config.data?.opacity} />

                                        <MdIcons.MdEdit size={18} style={{
                                            color: "var(--text-secondary-color)", cursor: "pointer", marginLeft: '3px', pointerEvents: 'auto'
                                        }}
                                            onMouseDown={() => {
                                                setSelectedAnnotation(Annotation.fromConfig(config))
                                                renderEditAnnotationModal(config, "EDIT")
                                            }}
                                        />

                                        <KeyboardButton id={`${config.id}_shortcut`} style={{ color: selectedShortcutIds.includes(config.id) ? "#FFFFFF" : "var(--color-accent-interactive)", display: config.isInEditMode ? "none" : "block", background: selectedShortcutIds.includes(config.id) ? "var(--color-accent-interactive)" : "var(--color-hover-neutral)", marginRight: '3px', marginLeft: 'auto' }}>
                                            {getPrettyHotkeyTrigger(config)}
                                        </KeyboardButton>
                                    </div>
                                </AnnotationDiv>
                            ))}
                        </ShortcutsContainer>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                            <h1>No Shortcuts</h1>
                        </div>
                    )
                )}

            </div>
        </div>)
}

const ModalContentVertical = styled.div`
    padding: 20px 20px 5px;
`

const ShortcutsContainer = styled.div`
    min-height: 550px;
    max-height: 550px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0px 20px;

    scrollbar-width: thin;
    ::-webkit-scrollbar {
        display: block;
        width: 8px;
        color: var(--color-scrollbar-thumb);
    }
    ::-webkit-scrollbar-track {
        background: var(--color-scrollbar-track);
        width: 8px;
        border-radius: 2px;
    }
`

const AnnotationDiv = styled.div`
    width: 100%;
    height: 24px;
    margin-bottom: 11px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: ${props => props.backgroundColor};
    p {
        font-family: 'Source Sans Pro';
        font-style: normal;
        font-weight: 400;
        font-size: 16px;
        line-height: 150%;
        color: var(--color-text-primary);
        margin-left: 3px;
    }
`;

const KeyboardButton = styled.div`
    height: 24px;
    width: fit-content;
    background: var(--color-hover-neutral);
    padding: 1.5px 10px;
    border-radius: 4px;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 150%;
    align-items: center;
    text-align: center;
    text-transform: lowercase;
    color: var(--text-secondary-color);
    margin-right: 3px;
`

// Utility function to convert hex color to rgb
function hexToRgb(hex) {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null
}

const ColorCircle = styled.div`
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background-image: linear-gradient(
        90deg,
        ${props => props.color} 50%,
        ${props => `rgba(${hexToRgb(props.color)}, ${props.opacity})`} 50%
    )
`